import JSZip from 'jszip'
import * as xlsx from 'xlsx-js-style'
import { SalesReportWithTenant } from 'src/slices/services/api'
import { formatEightDigitDate } from 'src/utils/date'
import { downloadBlob } from 'src/utils/download'
import { generateCSVData } from './downloadCSV'
import { DownloadResult } from './type'

// Types
interface DownloadOptions {
  workbook: xlsx.WorkBook
  orgCode: string
  storeCode: string
  storeName: string
  date: string
  salesReports: SalesReportWithTenant[]
}

/**
 * ワークブックをBlobに変換する内部関数
 */
const convertWorkbookToBlob = (
  workbook: xlsx.WorkBook
): { blob?: Blob; success: boolean } => {
  try {
    const buffer = xlsx.write(workbook, {
      type: 'array',
      bookType: 'xlsx',
    })
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    return { blob, success: true }
  } catch {
    return { success: false }
  }
}

/**
 * CSV・Excelファイルを含むZIPファイルをダウンロードする
 * @param options - ダウンロード設定オプション
 * @returns ダウンロード結果 { success: boolean, message: string }
 */
export const downloadCsvExcelZip = async ({
  workbook,
  orgCode,
  storeCode,
  storeName,
  date,
  salesReports,
}: DownloadOptions): Promise<DownloadResult> => {
  try {
    const zip = new JSZip()
    const formattedDate = formatEightDigitDate(date)

    // CSVファイルを生成してZIPに追加
    const csvData = generateCSVData(orgCode, storeCode, salesReports)

    const csvBlob = new Blob([csvData], { type: 'text/csv;charset=utf-8' })
    const csvFileName = `NIPPOU_${formattedDate}.csv`
    zip.file(csvFileName, csvBlob)

    // ExcelファイルをBlobに変換
    const blobResult = convertWorkbookToBlob(workbook)
    if (!blobResult.success || !blobResult.blob) {
      return {
        success: false,
        message: 'ZIP(CSV・Excel)ファイルの生成に失敗しました',
      }
    }

    // ExcelファイルをZIPに追加
    const excelFileName = `${storeName}_検証シート_${formattedDate}.xlsx`
    zip.file(excelFileName, blobResult.blob)

    // ZIPファイルを生成してダウンロード
    let zipBlob: Blob
    try {
      // NOTE: 同期版のgenerateは非推奨
      zipBlob = await zip.generateAsync({ type: 'blob' })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'ZIP generation failed'
      return {
        success: false,
        message: `ZIP(CSV・Excel)ファイルの生成に失敗しました: ${message}`,
      }
    }

    const zipFileName = `${storeName}_NIPPOU_${formattedDate}.zip`

    // ファイルダウンロードを実行
    downloadBlob(zipBlob, zipFileName)

    return {
      success: true,
      message: `ZIP（CSV・Excel）ファイル（${zipFileName}）のダウンロードが完了しました`,
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unexpected error'
    return {
      success: false,
      message: `ZIP(CSV・Excel)ファイルの生成に失敗しました: ${message}`,
    }
  }
}
